import React from "react";
import { Link } from "react-router-dom";
import { MdQuiz, MdArrowForward } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import { Skeleton } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import { instructorQuizzesService, type CourseQuiz } from "services/instructorQuizzesService";

export default function QuizzesPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { course, setCounts } = useCourse();
  const [quizzes, setQuizzes] = React.useState<CourseQuiz[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await instructorQuizzesService.listForCourse(course.id);
        if (cancelled) return;
        setQuizzes(list);
        setCounts({ quizzes: list.length });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [course.id, setCounts]);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {isArabic ? "الاختبارات" : "Quizzes"}
        </h2>
        <p className="text-sm text-gray-500">
          {isArabic
            ? "إدارة اختبارات الكورس ومعاينتها. تُنشأ الاختبارات من المحتوى."
            : "Manage and preview quizzes. Quizzes are created from the Content tab."}
        </p>
      </header>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : quizzes.length === 0 ? (
        <EmptyState
          icon={<MdQuiz className="h-6 w-6" />}
          title={isArabic ? "لا توجد اختبارات" : "No quizzes yet"}
          description={
            isArabic
              ? "أنشئ اختبارًا من تبويب المحتوى عبر إضافة عنصر من نوع quiz."
              : "Create a quiz from the Content tab by adding a quiz item."
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {quizzes.map((q) => (
            <Link
              key={q.id}
              to={`${q.id}`}
              className="group flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-300 dark:border-white/10 dark:bg-navy-800"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-200">
                <MdQuiz className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-gray-800 dark:text-white">{q.title}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {isArabic ? "درجة النجاح" : "Pass"}: {q.pass_degree} ·{" "}
                  {isArabic ? "المحاولات" : "Attempts"}: {q.max_attempts}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {isArabic ? "الأسئلة" : "Questions"}: {q.questions?.length ?? 0}
                </p>
              </div>
              <MdArrowForward
                className={`h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 ${
                  isArabic ? "rotate-180" : ""
                }`}
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
